import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useShopping } from '../../hooks/useShopping';
import { 
  Calendar, 
  DollarSign, 
  ShoppingCart, 
  TrendingUp,
  Filter,
  Download,
  BarChart3,
  List,
  Star,
  Plus,
  Check,
  X,
  ChevronLeft,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { getSuggestedItems } from '../../utils/smartItemMatcher';

export default function ShoppingHistory() {
  const { family } = useFamilyContext();
  const { history, loading, lists, createList, addItem, categorizeItem } = useShopping();
  const [timeFilter, setTimeFilter] = useState('30'); // days
  const [memberFilter, setMemberFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'frequency'
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedListId, setSelectedListId] = useState('');
  const [newListName, setNewListName] = useState('');
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);
  const [addingToList, setAddingToList] = useState(false);

  // Filter history - ensure history is an array
  const filteredHistory = (history || []).filter(item => {
    const itemDate = new Date(item.purchase_date);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(timeFilter));

    if (itemDate < cutoffDate) return false;
    if (memberFilter !== 'all' && item.purchased_by !== memberFilter) return false;
    if (categoryFilter !== 'all' && item.category_name !== categoryFilter) return false;

    return true;
  });

  // Calculate stats
  const stats = {
    totalItems: filteredHistory.length,
    totalSpent: filteredHistory.reduce((sum, item) => sum + (item.purchase_price || 0), 0),
    averagePrice: filteredHistory.length > 0 
      ? filteredHistory.reduce((sum, item) => sum + (item.purchase_price || 0), 0) / filteredHistory.length 
      : 0,
    topCategory: (() => {
      const categories = {};
      filteredHistory.forEach(item => {
        const cat = item.category_name || 'Other';
        categories[cat] = (categories[cat] || 0) + 1;
      });
      const sorted = Object.entries(categories).sort(([,a], [,b]) => b - a);
      return sorted[0]?.[0] || 'None';
    })()
  };

  // Group by date
  const groupedHistory = filteredHistory.reduce((groups, item) => {
    const date = format(new Date(item.purchase_date), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  // Intelligent item grouping and frequency analysis
  const frequentItems = getSuggestedItems(filteredHistory, 20);

  // Add to list handlers
  const handleAddToList = (item) => {
    setSelectedItem(item);
    setSelectedListId(lists.length > 0 ? lists[0].id : '');
    setShowAddToListModal(true);
  };

  const handleConfirmAddToList = async () => {
    if (!selectedItem) return;
    
    setAddingToList(true);
    
    try {
      let listId = selectedListId;
      
      // Create new list if needed
      if (isCreatingNewList && newListName.trim()) {
        const newList = await createList({
          name: newListName.trim(),
          description: `Created for ${selectedItem.name}`
        });
        listId = newList.id;
      }
      
      if (!listId) {
        throw new Error('No list selected');
      }
      
      // Add item to list with smart categorization
      const category = await categorizeItem(selectedItem.name);
      
      await addItem(listId, {
        name: selectedItem.name,
        quantity: 1,
        unit: 'item',
        category_id: category?.id,
        notes: `Based on purchase history (bought ${selectedItem.purchaseCount} times)`,
        priority: 'medium',
        estimated_price: selectedItem.averagePrice > 0 ? selectedItem.averagePrice : null
      });
      
      // Reset modal state
      setShowAddToListModal(false);
      setSelectedItem(null);
      setNewListName('');
      setIsCreatingNewList(false);
      
    } catch (error) {
      console.error('Error adding item to list:', error);
    } finally {
      setAddingToList(false);
    }
  };

  const handleCancelAddToList = () => {
    setShowAddToListModal(false);
    setSelectedItem(null);
    setNewListName('');
    setIsCreatingNewList(false);
  };

  // Header component
  const renderHeader = () => (
    <div className="bg-white border-b border-stone-200 sticky top-0 z-50">

      

    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-600 mx-auto mb-4"></div>
          <p className="text-app-secondary">Loading shopping history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {renderHeader()}
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-base">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-sm mb-1">Items Purchased</p>
                <p className="stats-number">{stats.totalItems}</p>
              </div>
              <div className="bg-action-subtle p-3 rounded-xl">
                <ShoppingCart className="w-6 h-6 text-action" />
              </div>
            </div>
          </div>

          <div className="card-base">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-sm mb-1">Total Spent</p>
                <p className="stats-number text-coral-600">${stats.totalSpent.toFixed(2)}</p>
              </div>
              <div className="bg-coral-50 p-3 rounded-xl">
                <DollarSign className="w-6 h-6 text-coral-600" />
              </div>
            </div>
          </div>

          <div className="card-base">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-sm mb-1">Average Price</p>
                <p className="stats-number text-coral-600">${stats.averagePrice.toFixed(2)}</p>
              </div>
              <div className="bg-coral-50 p-3 rounded-xl">
                <TrendingUp className="w-6 h-6 text-coral-600" />
              </div>
            </div>
          </div>

          <div className="card-base">
            <div className="flex items-center justify-between">
              <div>
                <p className="label-sm mb-1">Top Category</p>
                <p className="text-lg font-bold text-coral-600 truncate">{stats.topCategory}</p>
              </div>
              <div className="bg-coral-50 p-3 rounded-xl">
                <Filter className="w-6 h-6 text-coral-600" />
              </div>
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-center">
          <div className="card-base p-2">
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  viewMode === 'timeline'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-app-secondary hover:text-app-primary hover:bg-stone-100'
                }`}
              >
                <List className="w-4 h-4" />
                Timeline View
              </button>
              <button
                onClick={() => setViewMode('frequency')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  viewMode === 'frequency'
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'text-app-secondary hover:text-app-primary hover:bg-stone-100'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Frequency View
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card-base">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="label-md">Time Period</label>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="input-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="365">Last year</option>
              </select>
            </div>

            <div>
              <label className="label-md">Family Member</label>
              <select
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                className="input-primary"
              >
                <option value="all">All Members</option>
                {family?.parents?.map(parent => (
                  <option key={parent.id} value={parent.id}>{parent.name}</option>
                ))}
                {family?.kids?.map(kid => (
                  <option key={kid.id} value={kid.id}>{kid.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-md">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="input-primary"
              >
                <option value="all">All Categories</option>
                {Array.from(new Set((history || []).map(item => item.category_name || 'Other')))
                  .sort()
                  .map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <button
                onClick={() => {
                  // TODO: Implement export functionality
                  console.log('Export history:', filteredHistory);
                }}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Content based on view mode */}
        {filteredHistory.length === 0 ? (
          <div className="card-base text-center py-12">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-stone-400 opacity-60" />
            <h3 className="heading-sm mb-2">No purchases found</h3>
            <p className="text-app-secondary mb-6">
              No items were purchased in the selected time period.
            </p>
            <Link
              to="/shopping"
              className="btn-primary inline-flex items-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" />
              Go Shopping
            </Link>
          </div>
        ) : viewMode === 'frequency' ? (
          /* Frequency View - Most Purchased Items */
          <div className="space-y-6">
            <div className="card-base overflow-hidden">
              <div className="border-b border-stone-200 pb-4 mb-6">
                <h3 className="heading-sm text-app-primary flex items-center gap-2">
                  <Star className="w-6 h-6 text-gold-500" />
                  Most Frequently Purchased Items
                </h3>
                <p className="text-app-secondary text-sm mt-1">
                  Items sorted by purchase frequency with intelligent matching
                </p>
              </div>
              
              <div className="space-y-4">
                {frequentItems.length === 0 ? (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-stone-400" />
                    <p className="text-app-secondary">No purchase patterns found yet.</p>
                  </div>
                ) : (
                  frequentItems.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-8 h-8 bg-coral-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-coral-600">#{index + 1}</span>
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-semibold text-app-primary">{item.name}</h4>
                          <div className="flex items-center gap-4 text-sm text-app-secondary mt-1">
                            <span className="flex items-center gap-1">
                              <ShoppingCart className="w-3 h-3" />
                              {item.purchaseCount} {item.purchaseCount === 1 ? 'time' : 'times'}
                            </span>
                            <span className="text-stone-400">•</span>
                            <span>{item.category}</span>
                            {item.averagePrice > 0 && (
                              <>
                                <span className="text-stone-400">•</span>
                                <span className="text-coral-600 font-medium">
                                  ~${item.averagePrice.toFixed(2)}
                                </span>
                              </>
                            )}
                          </div>
                          
                          {item.variants.length > 1 && (
                            <div className="mt-2">
                              <details className="text-xs text-app-tertiary">
                                <summary className="cursor-pointer hover:text-app-secondary">
                                  {item.variants.length} variants found
                                </summary>
                                <div className="mt-1 pl-2 border-l-2 border-stone-200">
                                  {item.variants.slice(0, 5).map((variant, i) => (
                                    <div key={i} className="py-1">{variant}</div>
                                  ))}
                                  {item.variants.length > 5 && (
                                    <div className="py-1 italic">
                                      +{item.variants.length - 5} more...
                                    </div>
                                  )}
                                </div>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="label-xs">Last bought</div>
                          <div className="text-sm font-medium text-app-primary">
                            {item.daysSinceLastPurchase === 0 ? 'Today' :
                             item.daysSinceLastPurchase === 1 ? 'Yesterday' :
                             `${item.daysSinceLastPurchase} days ago`}
                          </div>
                        </div>
                        
                        <button 
                          onClick={() => handleAddToList(item)}
                          className="btn-primary text-sm flex items-center gap-1 px-3 py-2"
                          title="Add to shopping list"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Timeline View - Original grouped by date */
          <div className="space-y-6">
            {Object.entries(groupedHistory)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, items]) => (
              <div key={date} className="card-base overflow-hidden">
                {/* Date Header */}
                <div className="border-b border-stone-200 pb-4 mb-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-app-primary flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-coral-600" />
                      {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                    </h3>
                    <div className="text-app-secondary text-sm">
                      {items.length} items • ${items.reduce((sum, item) => sum + (item.purchase_price || 0), 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-3">
                  {items.map(item => {
                    const purchaser = [...(family?.parents || []), ...(family?.kids || [])]
                      .find(member => member.id === item.purchased_by);

                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-xl">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="text-lg">
                            {(() => {
                              const categoryEmojis = {
                                'Produce': '🥬',
                                'Dairy & Eggs': '🥛',
                                'Meat & Seafood': '🥩',
                                'Bakery': '🥖',
                                'Frozen': '🧊',
                                'Pantry': '🥫',
                                'Beverages': '🥤',
                                'Household': '🧹',
                                'Personal Care': '🧴',
                                'Baby': '👶',
                                'Pet Supplies': '🐕'
                              };
                              return categoryEmojis[item.category_name] || '📦';
                            })()}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-app-primary">{item.item_name}</h4>
                            <div className="flex items-center gap-4 text-sm text-app-secondary">
                              <span>Qty: {item.quantity} {item.unit}</span>
                              <span className="text-stone-400">•</span>
                              <span>{item.category_name || 'Other'}</span>
                              {item.store && (
                                <>
                                  <span className="text-stone-400">•</span>
                                  <span>{item.store}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {item.purchase_price && (
                            <div className="text-right">
                              <div className="font-semibold text-coral-600">
                                ${item.purchase_price.toFixed(2)}
                              </div>
                            </div>
                          )}

                          {purchaser && (
                            <div className="flex items-center gap-2">
                              <span className="label-xs">by</span>
                              <AvatarOrInitial 
                                avatar={purchaser.avatar} 
                                name={purchaser.name} 
                                size="sm"
                              />
                              <span className="text-sm text-app-secondary">{purchaser.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add to List Modal */}
      {showAddToListModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="card-base w-full max-w-md">
            <h3 className="heading-sm mb-4">
              Add "{selectedItem?.name}" to Shopping List
            </h3>

            <div className="space-y-4">
              {/* List Selection */}
              <div>
                <label className="label-md">Choose a list:</label>
                
                {/* Toggle between existing list and new list */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setIsCreatingNewList(false)}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                      !isCreatingNewList
                        ? 'bg-coral-100 text-coral-700 border border-coral-200'
                        : 'bg-stone-100 text-app-secondary border border-stone-200'
                    }`}
                  >
                    Existing List
                  </button>
                  <button
                    onClick={() => setIsCreatingNewList(true)}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                      isCreatingNewList
                        ? 'bg-coral-100 text-coral-700 border border-coral-200'
                        : 'bg-stone-100 text-app-secondary border border-stone-200'
                    }`}
                  >
                    New List
                  </button>
                </div>

                {!isCreatingNewList ? (
                  // Existing list selection
                  <div>
                    {lists.length > 0 ? (
                      <select
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}
                        className="input-primary"
                      >
                        {lists.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-center py-4 text-app-tertiary">
                        <p>No existing lists found.</p>
                        <p className="text-sm">Create a new list to add this item.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // New list creation
                  <div>
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="Enter list name..."
                      className="input-primary"
                    />
                  </div>
                )}
              </div>

              {/* Item Preview */}
              <div className="bg-stone-50 rounded-lg p-3">
                <h4 className="font-medium text-app-primary mb-2">Item Details:</h4>
                <div className="text-sm text-app-secondary space-y-1">
                  <div>Name: {selectedItem?.name}</div>
                  <div>Category: {selectedItem?.category}</div>
                  {selectedItem?.averagePrice > 0 && (
                    <div>Estimated Price: ${selectedItem.averagePrice.toFixed(2)}</div>
                  )}
                  <div>
                    Purchase History: {selectedItem?.purchaseCount} {selectedItem?.purchaseCount === 1 ? 'time' : 'times'}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancelAddToList}
                disabled={addingToList}
                className="btn-secondary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleConfirmAddToList}
                disabled={addingToList || (isCreatingNewList && !newListName.trim()) || (!isCreatingNewList && lists.length === 0)}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingToList ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Add to List
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}